import React, { useState, useEffect, useCallback } from 'react';
import { Editor, Range } from '@tiptap/core';
import { 
  Type, 
  Heading1, 
  Heading2, 
  Heading3, 
  List, 
  ListOrdered,
  CheckSquare,
  Quote,
  Minus,
  Code
} from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CommandMenuItem {
  title: string;
  command: ({ editor, range }: { editor: Editor; range: Range }) => void;
}

interface CommandMenuSimpleProps {
  items: CommandMenuItem[];
  editor: Editor;
  range: Range;
  onSelect: (item: CommandMenuItem) => void;
  onClose: () => void;
  query?: string;
}

export default function CommandMenuSimple({ items, editor, range, onSelect, onClose, query = '' }: CommandMenuSimpleProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    setSelectedIndex(0);
  }, [items]);

  const getItemIcon = (title: string) => {
    switch (title) {
      case 'Text':
        return <Type className="h-4 w-4" />;
      case 'Heading 1':
        return <Heading1 className="h-4 w-4" />;
      case 'Heading 2':
        return <Heading2 className="h-4 w-4" />;
      case 'Heading 3':
        return <Heading3 className="h-4 w-4" />;
      case 'Bulleted list':
        return <List className="h-4 w-4" />;
      case 'Numbered list':
        return <ListOrdered className="h-4 w-4" />;
      case 'To-do list':
        return <CheckSquare className="h-4 w-4" />;
      case 'Quote':
        return <Quote className="h-4 w-4" />;
      case 'Divider':
        return <Minus className="h-4 w-4" />;
      case 'Code':
        return <Code className="h-4 w-4" />;
      default:
        return <Type className="h-4 w-4" />;
    }
  };

  const getItemSymbol = (title: string) => {
    switch (title) {
      case 'Text':
        return '';
      case 'Heading 1':
        return '#';
      case 'Heading 2':
        return '##';
      case 'Heading 3':
        return '###';
      case 'Bulleted list':
        return '-';
      case 'Numbered list':
        return '1.';
      case 'To-do list':
        return '[]';
      case 'Quote':
        return '>';
      case 'Divider':
        return '---';
      case 'Code':
        return '`';
      default:
        return '';
    }
  };

  const handleItemClick = useCallback((item: CommandMenuItem, index: number) => {
    onSelect(item);
  }, [onSelect]);

  const scrollToIndex = useCallback((index: number) => {
    const menuElement = document.querySelector('[data-command-menu]');
    const itemElement = menuElement?.querySelector(`[data-item-index="${index}"]`) as HTMLElement;
    
    if (itemElement && menuElement) {
      itemElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, []);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!items.length) return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        const prevIndex = selectedIndex <= 0 ? items.length - 1 : selectedIndex - 1;
        setSelectedIndex(prevIndex);
        scrollToIndex(prevIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        const nextIndex = selectedIndex >= items.length - 1 ? 0 : selectedIndex + 1;
        setSelectedIndex(nextIndex);
        scrollToIndex(nextIndex);
        break;
      case 'Enter':
        event.preventDefault();
        if (items[selectedIndex]) {
          handleItemClick(items[selectedIndex], selectedIndex);
        }
        break;
      case 'Escape':
        event.preventDefault();
        onClose();
        break;
    }
  }, [items, selectedIndex, handleItemClick, onClose, scrollToIndex]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!items.length) return null;

  return (
    <div 
      data-command-menu
      className="z-50 min-w-[280px] max-w-[320px] overflow-hidden rounded-lg bg-background animate-in fade-in-0 slide-in-from-top-2 border shadow-lg" 
    >
      <div className="p-2">
        <div className="text-xs font-medium text-muted-foreground mb-3 px-2">Basic blocks</div>
        <div className="space-y-1">
          {items.map((item, index) => (
            <div
              key={index}
              data-item-index={index}
              onClick={() => handleItemClick(item, index)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={cn(
                'flex items-center justify-between px-3 py-2.5 rounded-md cursor-pointer transition-colors text-sm',
                index === selectedIndex 
                  ? 'bg-accent text-accent-foreground' 
                  : 'hover:bg-accent/50 text-foreground'
              )}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "flex items-center justify-center w-8 h-8 rounded border text-xs font-medium",
                  index === selectedIndex 
                    ? "border-accent-foreground/20 bg-accent-foreground/10 text-accent-foreground" 
                    : "border-border/50 bg-muted/50 text-muted-foreground"
                )}>
                  {getItemIcon(item.title)}
                </div>
                <span className="font-medium">{item.title}</span>
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                {getItemSymbol(item.title)}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-border/20 px-4 py-3 bg-muted/20">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <span className="text-muted-foreground/80">Type</span>
            <kbd className="inline-flex items-center justify-center px-2 py-1 text-xs font-mono bg-background border border-border/40 rounded shadow-sm min-w-[24px] h-[24px]">
              /
            </kbd>
            <span className="text-muted-foreground/80">on the page</span>
          </div>
          <kbd className="inline-flex items-center justify-center px-2 py-1 text-xs font-mono bg-background border border-border/40 rounded shadow-sm min-h-[24px]">
            esc
          </kbd>
        </div>
      </div>
    </div>
  );
}